import { ReactNode, useEffect, useMemo } from 'react'
import {
  initialQueryState,
  OptionsHelper,
  useDebounce,
} from '../../../../../../../_metronic/helpers'
import {
  ActiveFilter,
  ActiveFiltersBar,
  CustomSelect,
  Input,
  IOption,
  TableFilter,
  TableFilters,
} from '../../../../../../components'
import { useQueryRequest } from '../../core/QueryRequestProvider'
import { useQueryResponse } from '../../core/QueryResponseProvider'
import { useListFilters } from '../../core/ListFiltersProvider'
import { useT } from '_metronic/i18n/Metronici18n'

type Props = {
  /** Conteúdo à direita da linha dos campos (ex.: barra de assinatura em lote). */
  actions?: ReactNode
}

export const AvaliacoesPendentesListSearchComponent = ({ actions }: Props) => {
  const { updateState } = useQueryRequest()
  const { response, isLoading } = useQueryResponse()
  const { filters, setFilter, clearFilters } = useListFilters()
  const { t } = useT()
  const total = response?.totalElements ?? 0

  const debouncedFilters = useDebounce(filters, 500)

  const search = useMemo(
    () =>
      [
        debouncedFilters.protocolo
          ? `atendimento.protocolo~*${debouncedFilters.protocolo.trim()}*`
          : '',
        debouncedFilters.empresa
          ? `atendimento.paciente.empresa:${debouncedFilters.empresa.value}`
          : '',
        debouncedFilters.paciente
          ? `atendimento.paciente.pessoa.nome~*${debouncedFilters.paciente.trim()}*`
          : '',
      ]
        .filter(Boolean)
        .join(','),
    [debouncedFilters]
  )

  useEffect(() => {
    updateState({ ...initialQueryState, search })
  }, [search, updateState])

  // Chips da faixa "Filtros ativos": cada × limpa só o seu campo.
  const activeFilters: ActiveFilter[] = [
    filters.paciente.trim() && {
      key: 'paciente',
      label: t('cols.paciente'),
      value: `“${filters.paciente.trim()}”`,
      onRemove: () => setFilter('paciente', ''),
    },
    filters.protocolo.trim() && {
      key: 'protocolo',
      label: t('cols.protocolo'),
      value: `“${filters.protocolo.trim()}”`,
      onRemove: () => setFilter('protocolo', ''),
    },
    filters.empresa && {
      key: 'empresa',
      label: t('empresa.label'),
      value: filters.empresa.label,
      onRemove: () => setFilter('empresa', undefined),
    },
  ].filter((filter): filter is ActiveFilter => Boolean(filter))

  return (
    <TableFilters
      actions={actions}
      footer={
        <ActiveFiltersBar
          filters={activeFilters}
          onClearAll={clearFilters}
          summary={
            isLoading
              ? 'Buscando…'
              : `${total} ${total === 1 ? 'avaliação pendente' : 'avaliações pendentes'}`
          }
        />
      }
    >
      <TableFilter label={t('cols.paciente')} wide>
        <Input
          placeholder='Digite o nome do paciente'
          value={filters.paciente}
          onChange={(e) => setFilter('paciente', e.target.value)}
        />
      </TableFilter>
      <TableFilter label={t('cols.protocolo')}>
        <Input
          placeholder='Digite o protocolo'
          value={filters.protocolo}
          onChange={(e) => setFilter('protocolo', e.target.value)}
        />
      </TableFilter>
      <TableFilter label={t('empresa.label')}>
        <CustomSelect
          entity='empresa'
          fetchOptions={OptionsHelper.getOptionsEmpresas}
          onChange={(empresa?: IOption<number>) => setFilter('empresa', empresa ?? undefined)}
          value={filters.empresa}
        />
      </TableFilter>
    </TableFilters>
  )
}
